import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import JoinzyLogo from './joinzy_new_just_logo.png';
export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const { t, language, changeLanguage, languageNames, availableLanguages } =
    useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "backdrop-blur-xl bg-[#0F1115]/80 border-b border-white/10 shadow-lg"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="navbar-logo"
          >
            {/* Logo Icon */}
            <img
              src={JoinzyLogo} // Tırnak işaretlerini kaldırdık, süslü parantez içine aldık
              alt="Joinzy"
              className="h-8 w-auto object-contain" // w-auto ekledik ki logo basık durmasın
            />
            <span className="font-montserrat font-black text-2xl md:text-3xl tracking-tight text-[#008080]">
              Joinzy
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <NavLink
              onClick={() => scrollToSection("features")}
              testId="nav-features"
            >
              {t("nav.features")}
            </NavLink>
            <NavLink
              onClick={() => scrollToSection("faq")}
              testId="nav-how-it-works"
            >
              {t("nav.howItWorks")}
            </NavLink>
            <Link
              to="/subscription"
              className="relative text-white/70 hover:text-white font-poppins text-sm font-medium transition-colors group"
              data-testid="nav-subscription"
            >
              Subscription
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#00E5FF] group-hover:w-full transition-all duration-300" />
            </Link>

            {/* Language Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                className="flex items-center gap-2 px-3 py-2 text-white/70 hover:text-white transition-colors font-poppins text-sm"
                data-testid="language-dropdown-trigger"
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase">{language}</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${isLangDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              <AnimatePresence>
                {isLangDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-40 backdrop-blur-xl bg-[#0F1115]/95 border border-white/10 rounded-xl shadow-2xl overflow-hidden"
                    data-testid="language-dropdown-menu"
                  >
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang}
                        onClick={() => {
                          changeLanguage(lang);
                          setIsLangDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left text-sm font-poppins transition-colors ${
                          language === lang
                            ? "bg-[#008080]/30 text-[#00E5FF]"
                            : "text-white/70 hover:bg-white/5 hover:text-white"
                        }`}
                        data-testid={`language-option-${lang}`}
                      >
                        {languageNames[lang]}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Download Button with Dropdown */}
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsDownloadOpen(!isDownloadOpen)}
                className="relative px-6 py-2.5 rounded-full font-poppins font-semibold text-sm overflow-hidden group flex items-center gap-2"
                style={{
                  background:
                    "linear-gradient(135deg, #008080 0%, #004D4D 100%)",
                  boxShadow: "0 0 20px rgba(0, 128, 128, 0.4)",
                }}
                data-testid="nav-download-button"
              >
                <span className="relative z-10 text-white">
                  {t("nav.download")}
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-white transition-transform ${isDownloadOpen ? "rotate-180" : ""}`}
                />
                <div className="absolute inset-0 bg-[#00E5FF]/20 opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.button>

              <AnimatePresence>
                {isDownloadOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-56 backdrop-blur-xl bg-[#0F1115]/95 border border-white/10 rounded-xl shadow-2xl overflow-hidden p-3"
                    data-testid="download-dropdown"
                  >
                    {/* App Store Button */}
                    <a
                      href="#"
                      className="flex items-center gap-3 p-3 rounded-lg bg-black hover:bg-gray-900 transition-colors mb-2"
                      data-testid="download-appstore"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="w-8 h-8 text-white"
                        fill="currentColor"
                      >
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                      </svg>
                      <div>
                        <p className="text-[10px] text-white/60">
                          Download on the
                        </p>
                        <p className="text-white font-semibold text-sm">
                          App Store
                        </p>
                      </div>
                    </a>

                    {/* Google Play Button */}
                    <a
                      href="#"
                      className="flex items-center gap-3 p-3 rounded-lg bg-black hover:bg-gray-900 transition-colors"
                      data-testid="download-playstore"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="w-8 h-8"
                        fill="currentColor"
                      >
                        <path
                          fill="#EA4335"
                          d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92z"
                        />
                        <path
                          fill="#FBBC04"
                          d="M17.556 8.248l-3.764 3.751 3.764 3.752 4.243-2.392c.655-.37.655-1.35 0-1.72l-4.243-2.391z"
                        />
                        <path
                          fill="#4285F4"
                          d="M3.609 1.814l10.183 10.185 3.764-3.75L5.808.407A1.125 1.125 0 0 0 5.128 0c-.6 0-1.148.337-1.519.814z"
                        />
                        <path
                          fill="#34A853"
                          d="M3.609 22.186l10.183-10.185 3.764 3.751-11.748 7.842a1.125 1.125 0 0 1-.68.406c-.6 0-1.148-.337-1.519-.814z"
                        />
                      </svg>
                      <div>
                        <p className="text-[10px] text-white/60">GET IT ON</p>
                        <p className="text-white font-semibold text-sm">
                          Google Play
                        </p>
                      </div>
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-white/80 hover:text-white transition-colors"
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden backdrop-blur-xl bg-[#0F1115]/95 border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              <MobileNavLink
                onClick={() => scrollToSection("features")}
                testId="mobile-nav-features"
              >
                {t("nav.features")}
              </MobileNavLink>
              <MobileNavLink
                onClick={() => scrollToSection("faq")}
                testId="mobile-nav-how-it-works"
              >
                {t("nav.howItWorks")}
              </MobileNavLink>
              <Link
                to="/subscription"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-left py-2 text-white/80 hover:text-white font-poppins text-base transition-colors"
                data-testid="mobile-nav-subscription"
              >
                Subscription
              </Link>

              {/* Mobile Language Selector */}
              <div className="py-2">
                <p className="text-white/50 text-xs font-mono uppercase tracking-wider mb-3">
                  Language
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        changeLanguage(lang);
                      }}
                      className={`px-3 py-2 rounded-lg text-sm font-poppins transition-colors ${
                        language === lang
                          ? "bg-[#008080] text-white"
                          : "bg-white/5 text-white/70 hover:bg-white/10"
                      }`}
                      data-testid={`mobile-language-option-${lang}`}
                    >
                      {lang.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile Download Buttons */}
              <div className="space-y-2 pt-2">
                <a
                  href="#"
                  className="flex items-center justify-center gap-3 p-3 rounded-xl bg-black"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="w-6 h-6 text-white"
                    fill="currentColor"
                  >
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  <span className="text-white font-semibold">App Store</span>
                </a>
                <a
                  href="#"
                  className="flex items-center justify-center gap-3 p-3 rounded-xl bg-black"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="w-6 h-6"
                    fill="currentColor"
                  >
                    <path
                      fill="#EA4335"
                      d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92z"
                    />
                    <path
                      fill="#FBBC04"
                      d="M17.556 8.248l-3.764 3.751 3.764 3.752 4.243-2.392c.655-.37.655-1.35 0-1.72l-4.243-2.391z"
                    />
                    <path
                      fill="#4285F4"
                      d="M3.609 1.814l10.183 10.185 3.764-3.75L5.808.407A1.125 1.125 0 0 0 5.128 0c-.6 0-1.148.337-1.519.814z"
                    />
                    <path
                      fill="#34A853"
                      d="M3.609 22.186l10.183-10.185 3.764 3.751-11.748 7.842a1.125 1.125 0 0 1-.68.406c-.6 0-1.148-.337-1.519-.814z"
                    />
                  </svg>
                  <span className="text-white font-semibold">Google Play</span>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

const NavLink = ({ children, onClick, testId }) => (
  <motion.button
    onClick={onClick}
    className="relative text-white/70 hover:text-white font-poppins text-sm font-medium transition-colors group"
    whileHover={{ y: -2 }}
    data-testid={testId}
  >
    {children}
    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#00E5FF] group-hover:w-full transition-all duration-300" />
  </motion.button>
);

const MobileNavLink = ({ children, onClick, testId }) => (
  <button
    onClick={onClick}
    className="block w-full text-left py-2 text-white/80 hover:text-white font-poppins text-base transition-colors"
    data-testid={testId}
  >
    {children}
  </button>
);
